import React, { useEffect, useState } from "react";

// Config
import { config } from "./app.config";

// Styles
import "@ionic/react/css/core.css";
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/typography.css";
import "./theme/app.scss";

// Components
import { IonApp, setupIonicReact } from "@ionic/react";
import { AuthenticatedRouter } from "./routers/AuthenticatedRouter";
import { DefaultRouter } from "./routers/DefaultRouter";
import { ViewportProvider } from "./utils/useViewport";

// Contexts
import { AuthenticatedUserContext } from "./contexts/AuthenticatedUserContext";
import { SettingsProvider } from "./contexts/SettingsContext";

// Services
import * as Sentry from "@sentry/browser";
import MemberService from "./services/MemberService";
import StorageService from "./services/StorageService";

/* Font Awesome */
import { SplashScreen } from "@capacitor/splash-screen";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { fad } from "@fortawesome/pro-duotone-svg-icons";
import { fal } from "@fortawesome/pro-light-svg-icons";
import { far } from "@fortawesome/pro-regular-svg-icons";
import { fas } from "@fortawesome/pro-solid-svg-icons";
import { CheckForUpdates } from "./components/CheckForUpdates";
import SendbirdConnector from "./components/SendbirdConnector";
import { ApplicationProvider } from "./contexts/ApplicationContext";

import { FCM } from "@capacitor-community/fcm";
import { Capacitor } from "@capacitor/core";
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from "@capacitor/push-notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { SetFavicon } from "./components/SetFavicon";
import { LoggedInUnapprovedRouter } from "./routers/LoggedInUnapprovedRouter";
import ApiService from "./services/ApiService";

// Font awesome libraries
library.add(fal);
library.add(far);
library.add(fas);
library.add(fad);
library.add(fab);

setupIonicReact({});

let debug = (...args: any[]) => {};

if (config.debug) {
  if (config.debug_step) {
    debug = (...args) => {
      window.alert(args.join(", "));
    };
  } else {
    debug = window.console.log.bind(window.console, "[DEBUG]: %s");
  }
}

export { debug };

const App = (props: any) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [member, setMember] = useState<any>(null);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState<boolean>(false);
  const [numUnreadMessages, setNumUnreadMessages] = useState<number | null>(null);
  const [numUnreadFiles, setNumUnreadFiles] = useState<number>(0);
  const [deviceToken, setDeviceToken] = useState<any>();
  const [fcmToken, setFcmToken] = useState<string | null>(null);

  const [setupDone, setSetupDone] = useState<boolean>(false);
  const [showAllFiles, setShowAllFiles] = useLocalStorageState("adminShowAllFiles", {
    defaultValue: false,
  });
  const [showAllCalendarEvents, setShowAllCalendarEvents] = useLocalStorageState("adminShowAllCalendarEvents", {
    defaultValue: false,
  });
  const history = useHistory();

  useEffect(() => {
    if (config.debug) {
      console.log("[OBJECT]: member = ", member);
    }
  }, [member]);

  useEffect(() => {
    const init = async () => {
      debug("App.init");
      const member = await MemberService.getMember();
      debug("App.init: member = ", member);
      let loggedIn = await StorageService.get({ key: "loggedIn" }).then((result) => result.value === "true");
      debug("App.init: loggedIn = ", loggedIn);

      if (loggedIn && member === null) {
        // If we're logged in but don't have a member, log out
        loggedIn = false;
        debug("App thought it was logged in but member was null. Set loggedIn to false");
      }

      setMember(member);
      setNumUnreadFiles(member?.unread_files || 0);
      setLoggedIn(loggedIn);

      await SplashScreen.hide();

      debug("App.init: member = ", member, " loggedIn = ", loggedIn);
    };

    init();
  }, [history]);

  useEffect(() => {
    if (member) {
      debug("App.init: set sentry user");

      Sentry.setUser({
        email: member.email,
        id: member.id,
        username: member.nickname,
      });
    }
  }, [member]);

  useEffect(() => {
    const push = async () => {
      if (Capacitor.isPluginAvailable("PushNotifications")) {
        debug("App.init: Push notifications available, registering...");

        if (Capacitor.isNativePlatform()) {
          PushNotifications.checkPermissions().then((res) => {
            if (res.receive !== "granted") {
              PushNotifications.requestPermissions().then((res) => {
                if (res.receive === "denied") {
                  debug("App.init: push Notification permission denied");
                } else {
                  debug("App.init: push Notification permission granted");
                  register(); // register after ios user explicitly grants permission
                }
              });
            } else {
              register(); // auto register (only works for android as ios needs to ask permission)
            }
          });
        }

        // On success, we should be able to receive notifications
        PushNotifications.addListener("registration", async (tokenValue: Token) => {
          debug("App.init: registered for push notifications, platform = ", Capacitor.getPlatform());

          const token = tokenValue.value;

          if (!deviceToken) {
            setDeviceToken(token);
          }

          if (!fcmToken) {
            if (Capacitor.getPlatform() === "ios") {
              const tokenRequest = await FCM.getToken();
              setFcmToken(tokenRequest.token);
            } else {
              setFcmToken(token);
            }
          }
        });

        // Some issue with our setup and push will not work
        PushNotifications.addListener("registrationError", (error: any) => {
          debug("App.init: error on registration: " + JSON.stringify(error));
        });

        PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
          debug("Push notification received", notification);
          // received a Sendbird notification, so update the unread badge
          //if (notification.data?.sendbird) {
          //updateUnreadBadge(state, setNumUnreadMessages);
          //}
        });

        // Action performed on a push notification
        PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
          debug("Notification received: ", notification.notification.data);
          if (notification.notification.data?.fcmlink) {
            const url = notification.notification.data.fcmlink;
            debug("FCM Link detected, opening", url);
            //history.push({ pathname: url });
            window.location.replace(url);
          } else if (notification.notification.data?.sendbird) {
            const url = "/chat/channels/" + notification.notification.data.sendbird.channel.channel_url;
            debug("Sendbird notification detected, opening " + url);
            //history.push({ pathname: url });
            window.location.replace(url);
          }
        }).catch((err) => {
          debug("Error adding push notification listener", err);
        });
      } else {
        debug("PushNotifications plugin not available, not registering...");
        //debug("Setting fcmToken to a dummy value to test device registration");
        //setFcmToken("desktop");
      }
    };

    const register = () => {
      debug("App.init: registering for push notifications");
      PushNotifications.register();
    };

    if (!setupDone) {
      push().then(() => {
        setSetupDone(true);
      });
    }
  }, [fcmToken, deviceToken, history, setFcmToken, setDeviceToken, setupDone, setSetupDone]);

  useEffect(() => {
    if (member && member.id && (fcmToken || deviceToken)) {
      debug("App.init: member, member.id and either fcmToken or deviceToken are set, registering device", fcmToken, deviceToken);

      ApiService.post(`members/${member.id}/devices`, {
        fcm: fcmToken,
        apns: deviceToken,
      })
        .then((response) => {
          debug("App.init: registered device", response);
        })
        .catch((err) => {
          debug("App.init: error registering device", err);
        });
    }
  }, [fcmToken, deviceToken, member]);

  const signOut = async () => {
    setLoggedIn(false);
    setMember(null);
    await StorageService.remove({ key: "loggedIn" });
    await StorageService.remove({ key: "user" });
    await StorageService.remove({ key: "api_token" });
    await MemberService.clearMember();
  };

  // Handle login state change.
  window.addEventListener("loggedIn", async (e: any) => {
    setLoggedIn(true);
    setMember(e.detail.member);
  });

  const queryClient = new QueryClient();

  return (
    <ViewportProvider>
      <CheckForUpdates />
      <ApplicationProvider>
        <QueryClientProvider client={queryClient}>
          <AuthenticatedUserContext.Provider
            value={{
              member,
              setMember,
              loggedIn,
              setLoggedIn,
              showSignOutConfirm,
              setShowSignOutConfirm,
              signOut,
              numUnreadMessages,
              setNumUnreadMessages,
              deviceToken,
              setDeviceToken,
              numUnreadFiles,
              setNumUnreadFiles,
              showAllFiles,
              showAllCalendarEvents,
              setShowAllFiles,
              setShowAllCalendarEvents,
            }}
          >
            <SettingsProvider>
              <IonApp>
                <SetFavicon />
                {loggedIn ? (
                  member.status === "approved" ? (
                    <>
                      <SendbirdConnector />
                      <AuthenticatedRouter />
                    </>
                  ) : (
                    <LoggedInUnapprovedRouter />
                  )
                ) : (
                  <DefaultRouter />
                )}
              </IonApp>
            </SettingsProvider>
          </AuthenticatedUserContext.Provider>
        </QueryClientProvider>
      </ApplicationProvider>
    </ViewportProvider>
  );
};

export default App;
